import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useInfra } from '../../infra/context.js';
import { uploadBulk, type BulkProgress } from './uploadBulk.js';
import { newSessionId } from './persistence.js';

export type UploadStatus = 'running' | 'completed' | 'error';

export interface UploadSession {
  id: string;
  albumId: string;
  albumLabel?: string;
  status: UploadStatus;
  progress: BulkProgress | null;
  error?: string;
  startedAt: number;
  finishedAt?: number;
}

export interface StartUploadInput {
  files: File[];
  albumId: string;
  albumKey: CryptoKey;
  token: string;
  albumLabel?: string;
  onCompleted?: () => void;
  onError?: (message: string) => void;
}

interface UploadContextValue {
  sessions: UploadSession[];
  startUpload: (input: StartUploadInput) => string;
  dismiss: (sessionId: string) => void;
  cancel: (sessionId: string) => void;
}

const UploadContext = createContext<UploadContextValue | null>(null);

const COMPLETED_AUTO_DISMISS_MS = 4000;

export function UploadProvider({ children }: { children: ReactNode }) {
  const { metadata, storage } = useInfra();
  const [sessions, setSessions] = useState<UploadSession[]>([]);
  const abortControllers = useRef(new Map<string, AbortController>());

  const dismiss = useCallback((sessionId: string) => {
    abortControllers.current.delete(sessionId);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  const cancel = useCallback((sessionId: string) => {
    const ac = abortControllers.current.get(sessionId);
    ac?.abort();
  }, []);

  const startUpload = useCallback(
    (input: StartUploadInput): string => {
      const sessionId = newSessionId();
      const ac = new AbortController();
      abortControllers.current.set(sessionId, ac);

      const baseSession: UploadSession = {
        id: sessionId,
        albumId: input.albumId,
        ...(input.albumLabel ? { albumLabel: input.albumLabel } : {}),
        status: 'running',
        progress: null,
        startedAt: Date.now(),
      };
      setSessions((prev) => [...prev, baseSession]);

      void (async () => {
        try {
          await uploadBulk({
            files: input.files,
            albumId: input.albumId,
            albumKey: input.albumKey,
            token: input.token,
            metadata,
            storage,
            sessionId,
            signal: ac.signal,
            onProgress: (snapshot) => {
              setSessions((prev) =>
                prev.map((s) => (s.id === sessionId ? { ...s, progress: snapshot } : s)),
              );
            },
          });
          setSessions((prev) =>
            prev.map((s) =>
              s.id === sessionId ? { ...s, status: 'completed', finishedAt: Date.now() } : s,
            ),
          );
          input.onCompleted?.();
          setTimeout(() => dismiss(sessionId), COMPLETED_AUTO_DISMISS_MS);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'errore upload';
          setSessions((prev) =>
            prev.map((s) =>
              s.id === sessionId
                ? { ...s, status: 'error', error: message, finishedAt: Date.now() }
                : s,
            ),
          );
          input.onError?.(message);
        } finally {
          abortControllers.current.delete(sessionId);
        }
      })();

      return sessionId;
    },
    [dismiss, metadata, storage],
  );

  useEffect(() => {
    const hasActive = sessions.some((s) => s.status === 'running');
    if (!hasActive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [sessions]);

  const value = useMemo<UploadContextValue>(
    () => ({ sessions, startUpload, dismiss, cancel }),
    [sessions, startUpload, dismiss, cancel],
  );

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUploads(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUploads must be used within UploadProvider');
  return ctx;
}

export function useAlbumUploads(albumId: string): UploadSession[] {
  const { sessions } = useUploads();
  return useMemo(() => sessions.filter((s) => s.albumId === albumId), [sessions, albumId]);
}
